import { jobSuggestionSearchQuery } from '../../graphql/queries/jobsSearch'
import QUERY_KEYS from '../../graphql/queryKeys'
import { captureError } from '../../utilities/errorHandlers'
import { individualMatches, SAME_AS_BILLING_ADDRESS } from '../search-suggest-input/individualMatches'

export const JOB_SUGGESTION_LIMIT = 5
const JOB_SUGGESTION_SORT = { column: 'customId', direction: 'desc' }

const FIELD_SUGGESTIONS = {
  customId: {
    // searchTerm also matches addresses and account names, so its first rows can hide a real job id.
    searchFields: (term) => ({ customId: term }),
    valuesFor: (job) => [job.customId]
  },
  streetAddress: {
    searchFields: (term) => ({ streetAddress: term }),
    valuesFor: (job) => [job.addressline1]
  },
  city: {
    searchFields: (term) => ({ city: term }),
    valuesFor: (job) => [job.city]
  },
  zipCode: {
    searchFields: (term) => ({ zipCode: term }),
    valuesFor: (job) => [job.zip]
  }
}

export function buildJobApiSearchFields (values = {}) {
  const term = typeof values.universalSearch === 'string' ? values.universalSearch.trim() : ''
  const streetAddress = values.streetAddress || null
  const city = values.city || null
  const zipCode = values.zipCode || null
  const customId = values.customId || null
  const termMatchesSpecificField = [streetAddress, city, zipCode, customId]
    .some((field) => field && String(field).trim() === term)
  const fields = {
    searchTerm: term && !termMatchesSpecificField ? term : null,
    clientId: values.clientId || null,
    customId,
    streetAddress,
    city,
    zipCode,
    status: values.status || null
  }

  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value))
}

export async function loadJobFieldSuggestions ({ dataLoaders, haulerId, term, field }) {
  const fieldSuggestion = FIELD_SUGGESTIONS[field]
  const needle = term.trim()
  if (!fieldSuggestion || !needle || !dataLoaders?.dispatcher || !haulerId) return []

  try {
    // Direct request, not useQuery, so typing does not refetch the results table or toast.
    const response = await jobSuggestionSearchQuery({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.jobsSearch,
        haulerId,
        JOB_SUGGESTION_LIMIT,
        JOB_SUGGESTION_SORT,
        fieldSuggestion.searchFields(needle),
        null
      ]
    })

    return fieldSuggestionItems(response?.jobs?.nodes || [], needle, fieldSuggestion.valuesFor)
  } catch (error) {
    captureError(error)
    return []
  }
}

function fieldSuggestionItems (jobs, term, valuesFor) {
  const matches = []
  const seen = new Set()

  jobs.forEach((job) => {
    individualMatches(term, valuesFor(job), [SAME_AS_BILLING_ADDRESS]).forEach((label) => {
      const key = label.toLowerCase()
      if (matches.length >= JOB_SUGGESTION_LIMIT || seen.has(key)) return
      seen.add(key)
      matches.push({
        id: `${job.id}-${label}`,
        label,
        value: label
      })
    })
  })

  return matches
}
